import { useState, useRef, useEffect } from "react";
import { SVG_ENUM } from "enums";
import {
  AssetCategorySlug,
  ASSET_CATEGORY_NAMES,
  getAssetCategorySvg,
} from "enums/AssetCategoryEnum";
import SVG from "shared/SVG";
import { isDesktop } from "utils";
import { useGetInvestmentTypes } from "hooks/investments";
import { InvestmentType } from "types/InvestmentTypes";
import "./AddAssetDropdown.scss";

interface AddAssetDropdownProps {
  onSelect: (slug: AssetCategorySlug) => void;
}

const AddAssetDropdown = ({ onSelect }: AddAssetDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: investmentTypes = [] } = useGetInvestmentTypes();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !isDesktop()) {
      document.documentElement.classList.add("modal-open");
    } else {
      document.documentElement.classList.remove("modal-open");
    }

    return () => {
      document.documentElement.classList.remove("modal-open");
    };
  }, [isOpen]);

  const handleSelect = (slug: AssetCategorySlug) => {
    onSelect(slug);
    setIsOpen(false);
  };

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <div className="add-asset-dropdown" ref={dropdownRef}>
      <button
        className={`add-asset-dropdown__trigger ${
          isOpen ? "add-asset-dropdown__trigger--open" : ""
        } button button--primary`}
        onClick={handleToggle}
      >
        Dodaj nowe aktywo +
      </button>

      {isOpen && (
        <>
          {isDesktop() ? (
            <div className="add-asset-dropdown__menu">
              <div className="add-asset-dropdown__header"></div>
              <ul className="add-asset-dropdown__list">
                {investmentTypes.map((type: InvestmentType) => (
                  <li
                    key={type.uuid}
                    className="add-asset-dropdown__item"
                    onClick={() => handleSelect(type.slug as AssetCategorySlug)}
                  >
                    {getAssetCategorySvg(type.slug) && (
                      <SVG
                        type={getAssetCategorySvg(type.slug)!}
                        className="add-asset-dropdown__icon"
                      />
                    )}
                    <span className="add-asset-dropdown__label">
                      {ASSET_CATEGORY_NAMES[type.slug as AssetCategorySlug] ??
                        type.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="add-asset-dropdown__overlay">
              <div className="add-asset-dropdown__mobile">
                <button
                  className="add-asset-dropdown__back"
                  onClick={handleClose}
                >
                  <SVG
                    type={SVG_ENUM.ARROW_LEFT}
                    className="add-asset-dropdown__back-icon"
                  />
                  <span>Wróć</span>
                </button>

                <div className="add-asset-dropdown__mobile-header">
                  <SVG
                    type={SVG_ENUM.SHARES}
                    className="add-asset-dropdown__mobile-header-icon"
                  />
                  <h2>Dodaj nowe aktywo</h2>
                </div>

                <ul className="add-asset-dropdown__list add-asset-dropdown__list--mobile">
                  {investmentTypes.map((type: InvestmentType) => (
                    <li
                      key={type.uuid}
                      className="add-asset-dropdown__item add-asset-dropdown__item--mobile"
                      onClick={() =>
                        handleSelect(type.slug as AssetCategorySlug)
                      }
                    >
                      {getAssetCategorySvg(type.slug) && (
                        <SVG
                          type={getAssetCategorySvg(type.slug)!}
                          className="add-asset-dropdown__icon"
                        />
                      )}
                      <span className="add-asset-dropdown__label">
                        {ASSET_CATEGORY_NAMES[type.slug as AssetCategorySlug] ??
                          type.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AddAssetDropdown;
